import React from "react";
import ToggleButtonGroup from "react-bootstrap/lib/ToggleButtonGroup";
import ToggleButton from "react-bootstrap/lib/ToggleButton";
import Button from "react-bootstrap/lib/Button";
import ButtonToolbar from "react-bootstrap/lib/ButtonToolbar";
import AbsoluteDropdown from "components/AbsoluteDropdown";
import Dropdown from "react-bootstrap/lib/Dropdown";
import MenuItem from "react-bootstrap/lib/MenuItem";
import CountBadge from "components/CountBadge";
import "font-awesome/css/font-awesome.css";
import FontAwesome from "react-fontawesome";
import styles from "./Toolbar.module.css";
import loc from "i18next";

const Views = {
    month: "dayGridMonth",
    week: "timeGridWeek",
    day: "timeGridDay",
    agenda: "listWeek",
};

type Props = {
    /** current calendar range title */
    title: string,
    /** ReactRef to FullCalendar instance */
    calendarRef: React.Ref,
    /** current FullCalendar view name */
    currentView: string,
    /** info on all projections */
    projections: Object,
    /** current selected projection
     * @default {string} "" to show ALL
     */
    currentProjection: string,
    /** callback when user selects a projection
     * @param {string} newProjection projection name from projections
     */
    onProjectionChange: (newProjection: string) => void,
    /** button rendering for loading next result set */
    nextPageButton: React.ReactElement,
    /** Browser info */
    browser: Object,
};

const Toolbar = ({
    title,
    browser,
    calendarRef,
    currentView,
    projections,
    currentProjection,
    onProjectionChange,
    nextPageButton,
}: Props) => (
    <div className={styles.toolbar}>
        <ButtonToolbar>
            <ButtonToolbar>
                {nextPageButton}

                <AbsoluteDropdown id="src-field" bsSize="small">
                    <Dropdown.Toggle chrome>
                        {loc.t("common:search.calendar.projections")}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {currentProjection !== "" && (
                            <MenuItem
                                onSelect={
                                    /* istanbul ignore next*/ () =>
                                        onProjectionChange("")
                                }
                            >
                                {loc.t("common:filter.AllCaption")}
                            </MenuItem>
                        )}
                        {Object.keys(projections).map((name) => (
                            <MenuItem
                                key={`src-proj-${name}`}
                                onSelect={
                                    /* istanbul ignore next*/ () =>
                                        onProjectionChange(name)
                                }
                                active={currentProjection === name}
                            >
                                <span className="pull-left text-sm">
                                    <FontAwesome
                                        fixedWidth
                                        name="circle"
                                        className={styles.projColor}
                                        style={{
                                            color: projections[name].color,
                                        }}
                                    />
                                </span>
                                <span className={styles.proName}>{name}</span>
                                <span className="pull-right text-sm">
                                    <CountBadge
                                        count={projections[name].total}
                                    />
                                </span>
                            </MenuItem>
                        ))}
                    </Dropdown.Menu>
                </AbsoluteDropdown>
            </ButtonToolbar>

            <ButtonToolbar name="srcviews">
                <Button
                    id="src-prev"
                    bsSize="small"
                    onClick={() => calendarRef.getApi().prev()}
                    title={loc.t("common:search.calendar.prev")}
                >
                    <FontAwesome name="arrow-left" />
                </Button>
                <Button
                    id="src-today"
                    bsSize="small"
                    onClick={() => calendarRef.getApi().today()}
                    title={loc.t("common:search.calendar.today")}
                >
                    <span className={styles.title}>{title}</span>
                </Button>
                <Button
                    id="src-next"
                    bsSize="small"
                    onClick={() => calendarRef.getApi().next()}
                    title={loc.t("common:search.calendar.next")}
                >
                    <FontAwesome name="arrow-right" />
                </Button>
            </ButtonToolbar>

            <ToggleButtonGroup
                name="srcviews"
                bsSize="small"
                defaultValue={currentView}
                onChange={
                    /* istanbul ignore next*/ (name) =>
                        calendarRef.getApi().changeView(name)
                }
            >
                {Object.keys(Views).map((name) => (
                    <ToggleButton
                        id={`src-view-${name}`}
                        key={`src-view-${name}`}
                        value={Views[name]}
                        title={loc.t(`common:search.calendar.${name}`)}
                    >
                        {loc
                            .t(`common:search.calendar.${name}`)
                            .substr(0, browser.lessThan.medium ? 1 : undefined)}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </ButtonToolbar>
    </div>
);
Toolbar.displayName = "Calendar.Toolbar";
export default Toolbar;
